import { cx } from "@emotion/css";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import routing from "routing";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { uploadHtmlBlobImages } from "src/helpers/uploadHtmlBlobImages";
import { useCreateDeck } from "src/hooks/useFlashcards";
import FlashcardIcon from "src/images/Icons/newIcons/searchTypes/fish.svg";
import { useQueryState } from "../../../hooks/useQueryState";
import useSearchParams from "../../../hooks/useSearchParams";
import { Header } from "../../Main/ExamsBox/ExamsBox";
import DefaultCreator from "./DefaultCreator";
import DeckFilters from "./Filters/DeckFilters";

interface DeckDetails {
	permission: string;
	description: string;
	flashcards: { id?: string; front: string; back: string }[];
	tagsIds: string[];
}

const initialState: DeckDetails = {
	permission: "public",
	description: "",
	flashcards: [],
	tagsIds: [],
};

// ponytail: draft autosave to localStorage — działa w Capacitor WebView (persystentny między sesjami).
// URL (?filters=) już trzyma stan przy reloadzie; localStorage łata utratę przy zamknięciu appki + zapisuje name.
const DRAFT_KEY = "deck-draft";

function NewDeckCreator() {
	const { t } = useTranslation();
	const history = useHistory();
	const search = useSearchParams();
	const { openModal } = useModals();
	const { mutateAsync: mutateAsyncDeck, isPending: isLoading } =
		useCreateDeck();
	const mutateAsync = mutateAsyncDeck as unknown as (
		payload: Record<string, unknown>,
	) => Promise<string>;
	const initialSelectedFolderIds = search?.folderId ? [search.folderId] : [];
	const [deckDetails, setDeckDetails] = useQueryState(initialState);
	const [name, setName] = useState<string | null>(null);
	const [isUploadingImages, setIsUploadingImages] = useState(false);

	// Zapis draftu robimy w handlerach zmiany (nie w efekcie) — zgodnie z "You Might Not Need an Effect".
	const saveDraft = (details: DeckDetails, nm: string | null) => {
		const isEmpty =
			details.flashcards.length === 0 && !nm && !details.description;
		if (isEmpty) {
			localStorage.removeItem(DRAFT_KEY);
		} else {
			localStorage.setItem(
				DRAFT_KEY,
				JSON.stringify({ deckDetails: details, name: nm }),
			);
		}
	};

	const updateDeck = (values: Partial<DeckDetails>) => {
		const next = { ...deckDetails, ...values };
		setDeckDetails(values);
		saveDraft(next, name);
	};

	const updateName = (nm: string | null) => {
		setName(nm);
		saveDraft(deckDetails, nm);
	};

	// Przy wejściu — jeśli URL nie ma stanu, a jest niezapisany draft, spytaj: przywrócić czy od nowa.
	useEffect(() => {
		if (search?.filters) return;
		try {
			const raw = localStorage.getItem(DRAFT_KEY);
			if (!raw) return;
			const draft = JSON.parse(raw);
			const hasContent = draft.deckDetails?.flashcards?.length || draft.name;
			if (!hasContent) return;
			openModal(ModalsNames.RestoreDeckDraftModal, {
				count: draft.deckDetails?.flashcards?.length ?? 0,
				onRestore: () => {
					if (draft.deckDetails) setDeckDetails(draft.deckDetails);
					if (draft.name) setName(draft.name);
				},
				onDiscard: () => localStorage.removeItem(DRAFT_KEY),
			});
		} catch {
			/* uszkodzony draft — ignoruj */
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isValid = (name: string) => {
		const { flashcards } = deckDetails;
		if (flashcards.length === 0) {
			toast.error(t("creators.decks.errors.emptyDeck"), {
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 3000,
			});
			return false;
		}

		if (!name) {
			toast.error(t("creators.decks.errors.nameRequired"), {
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 3000,
			});
			return false;
		}

		return true;
	};

	const onSubmit = async (name: string, folderIds: string[]) => {
		if (!isValid(name)) {
			return;
		}

		try {
			setIsUploadingImages(true);
			const flashcards = await Promise.all(
				deckDetails.flashcards
					.filter((flashcard) => !flashcard.id)
					.map(async ({ front, back }) => ({
						front: await uploadHtmlBlobImages(front),
						back: await uploadHtmlBlobImages(back),
					})),
			);
			setIsUploadingImages(false);

			const deckId = await mutateAsync({
				name,
				permission: deckDetails.permission,
				flashcards,
				...(deckDetails.description
					? { description: deckDetails.description }
					: {}),
				...(folderIds.length ? { folderIds } : {}),
			});

			localStorage.removeItem(DRAFT_KEY);
			history.push(generatePath(routing.paths.flashcardsApp, { deckId }));
		} catch {
			toast.error(
				t("creators.decks.errors.saveError", "Nie udało się zapisać zestawu"),
				{
					position: toast.POSITION.BOTTOM_LEFT,
					pauseOnHover: false,
					autoClose: 5000,
				},
			);
		} finally {
			setIsUploadingImages(false);
		}
	};

	const renderFilters = () => {
		return (
			<DeckFilters
				deckDetails={deckDetails}
				setData={updateDeck}
				onImportName={(importedName) => {
					if (importedName && !name) {
						updateName(importedName);
					}
				}}
				isLoading={false}
			/>
		);
	};

	return (
		<DefaultCreator
			className="flashcard--deck--creator"
			count={deckDetails.flashcards.length}
			onSubmit={onSubmit}
			name={name}
			onNameChange={updateName}
			renderFilters={renderFilters}
			inputLabel={t("creators.decks.deckName", "Nazwa zestawu")}
			inputPlaceholder={t("creators.decks.inputPlaceholder")}
			counterText={t("creators.decks.counterText")}
			isLoading={isLoading || isUploadingImages}
			getPrimaryButtonText={() => {
				if (isUploadingImages) {
					return t("creators.decks.uploadingImages", "Wysyłam zdjęcia...");
				}
				if (isLoading) {
					return t("creators.decks.creating");
				}
				return t("creators.decks.createDeck", "Utwórz zestaw");
			}}
			Header={
				<Header
					title={t("creators.decks.title")}
					icon={<FlashcardIcon className={cx("icon-super-big icon-purple")} />}
					withBackButton
				/>
			}
			reset={() => {
				setDeckDetails(initialState);
				setName(null);
				localStorage.removeItem(DRAFT_KEY);
			}}
			initialSelectedFolderIds={initialSelectedFolderIds}
		/>
	);
}

export default NewDeckCreator;
